import { Box, Typography } from "@mui/material";
import NextLink from "next/link";
import Kicker from "@/src/components/common/broadsheet/kicker";
import Panel from "@/src/components/common/broadsheet/panel";
import {
  FONT_DISPLAY,
  FONT_TEXT,
  FONT_UI,
  hairline,
  hairlineTop,
} from "@/src/components/common/broadsheet/tokens";

const COLUMN_BREAK_SM = 576;
const COLUMN_BREAK_LG = 992;

const bandColumns = (count: number) => `repeat(${count}, minmax(0, 1fr))`;

const bandAt = (columns: number) => ({
  gridTemplateColumns: bandColumns(columns),
  [`& > *:nth-child(${columns}n + 1)`]: { borderLeftWidth: 0, pl: 0 },
  [`& > *:nth-child(-n + ${columns})`]: { borderTopWidth: 0, pt: 0 },
});

type Metric = {
  label: string;
  value?: string | number | null;
  suffix?: string;
  link?: string | null;
  lead?: boolean;
};

const Row = ({ metric }: { metric: Metric }) => {
  const value = `${metric.value}${metric.suffix ?? ""}`;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "baseline",
        justifyContent: "space-between",
        gap: 0.5,
        py: metric.lead ? 0.5 : 0.3125,
        ...hairline,
      }}
    >
      <Typography
        component="span"
        sx={{
          fontFamily: FONT_UI,
          fontSize: metric.lead ? "0.75rem" : "0.71875rem",
          fontWeight: metric.lead ? 700 : 400,
          lineHeight: 1.4,
          color: "custom.inkMuted",
        }}
      >
        {metric.label}
      </Typography>
      <Typography
        component="span"
        sx={{
          flex: "none",
          fontFamily: metric.lead ? FONT_DISPLAY : FONT_TEXT,
          fontSize: metric.lead ? "1.375rem" : "0.875rem",
          fontWeight: 700,
          lineHeight: 1.1,
          fontVariantNumeric: "tabular-nums",
          color: metric.link ? "custom.link" : "custom.ink",
          "& a": {
            color: "inherit",
            textDecoration: "none",
            "&:hover": {
              textDecoration: "underline",
              textUnderlineOffset: "0.15em",
            },
          },
        }}
      >
        {metric.link ? <NextLink href={metric.link}>{value}</NextLink> : value}
      </Typography>
    </Box>
  );
};

const Tile = ({ metric }: { metric: Metric }) => {
  const value = `${metric.value}${metric.suffix ?? ""}`;

  return (
    <Box>
      <Typography
        component="p"
        sx={{
          m: 0,
          mb: 0.125,
          fontFamily: FONT_UI,
          fontSize: "0.75rem",
          fontWeight: 700,
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          lineHeight: 1.4,
          color: "custom.inkMuted",
        }}
      >
        {metric.label}
      </Typography>
      <Typography
        component="p"
        sx={{
          m: 0,
          fontFamily: FONT_DISPLAY,
          fontWeight: 700,
          fontSize: { xs: "1.5rem", md: "1.875rem" },
          lineHeight: 1.15,
          fontVariantNumeric: "tabular-nums",
          color: metric.link ? "custom.link" : "custom.ink",
          "& a": {
            color: "inherit",
            textDecoration: "none",
            "&:hover": {
              textDecoration: "underline",
              textUnderlineOffset: "0.15em",
            },
          },
        }}
      >
        {metric.link ? <NextLink href={metric.link}>{value}</NextLink> : value}
      </Typography>
    </Box>
  );
};

const present = (metric: Metric) =>
  metric.value !== null && metric.value !== undefined && metric.value !== "";

const JournalMetrics = ({
  journal,
  variant = "list",
}: {
  journal: Journal;
  variant?: "list" | "band";
}) => {
  const meta = journal.meta_data ?? ({} as any);
  const configs = journal.options?.template_configs?.journal_metrics?.metrics;

  const metrics: Metric[] = [
    {
      label: meta.if_score_year ? `JIF ${meta.if_score_year}` : "JIF",
      value: meta.if_score,
      link: meta.if_score_link,
      lead: true,
    },
    {
      label: meta.citation_score_year
        ? `CiteScore ${meta.citation_score_year}`
        : "CiteScore",
      value: meta.citation_score,
      link: meta.citation_score_link,
      lead: true,
    },
    {
      label: "Time to first decision",
      value: meta.time_to_first_decision,
      suffix: " days",
      link: meta.time_to_first_decision_link,
    },
    {
      label: "Review time",
      value: meta.review_time,
      suffix: " days",
      link: meta.review_time_link,
    },
    {
      label: "Accept to publication",
      value: meta.publication_time,
      suffix: " days",
      link: meta.publication_time_link,
    },
    {
      label: "Acceptance rate",
      value: meta.acceptance_rate,
      suffix: "%",
      link: meta.acceptance_rate_link,
    },
    {
      label: "Open peer review",
      value: meta.open_peer_review,
      link: meta.open_peer_review_link,
    },
    { label: "ESCI", value: meta.esci, link: meta.esci_link },
    { label: "PubMed Central", value: meta.pmc, link: meta.pmc_link },
    {
      label: "Processing charges",
      value: meta.acceptance_fee,
      link: meta.acceptance_fee_link,
    },
  ].filter(present);

  if (metrics.length === 0) return null;

  return (
    <Box component="section" aria-labelledby="journal-metrics-heading">
      <Kicker component="p" id="journal-metrics-heading" sx={{ mb: 0.25 }}>
        {configs?.label ?? "Journal metrics"}
      </Kicker>
      {configs?.caption ? (
        <Typography
          component="p"
          sx={{
            m: 0,
            mb: 0.375,
            fontFamily: FONT_TEXT,
            fontSize: "0.8125rem",
            lineHeight: 1.5,
            color: "custom.inkMuted",
          }}
        >
          {configs.caption}
        </Typography>
      ) : null}
      {variant === "band" ? (
        <Panel>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: bandColumns(2),
              "& > *": {
                minWidth: 0,
                py: 0.75,
                px: 1.25,
                borderLeft: "1px solid",
                ...hairlineTop,
              },
              [`@media (max-width:${COLUMN_BREAK_SM - 0.05}px)`]: bandAt(2),
              [`@media (min-width:${COLUMN_BREAK_SM}px) and (max-width:${
                COLUMN_BREAK_LG - 0.05
              }px)`]: bandAt(3),
              [`@media (min-width:${COLUMN_BREAK_LG}px)`]: bandAt(4),
            }}
          >
            {metrics.map((metric) => (
              <Tile key={metric.label} metric={metric} />
            ))}
          </Box>
        </Panel>
      ) : (
        <Box>
          {metrics.map((metric) => (
            <Row key={metric.label} metric={metric} />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default JournalMetrics;
